// propertyContextBridge — the narrowest safe natural-language entry bridge
// for the Property visual room (Intentional Propulsion Slice 6, issue #169
// Slice 6A). Janet's full tool-calling loop (api/janet-chat.js,
// api/janet-ask.js) has no generic "open this record" action today — every
// action type it can return is task/draft/call/calendar-specific (see
// api/janet-chat.js's submit_response tool schema). Building a whole new
// tool-call contract into that 1800-line streaming hot path is out of scope
// for one bounded slice, so this is the documented "narrowest safe bridge"
// instead: a DETERMINISTIC phrase match, resolved against the SAME
// properties table search_all already reads (title/search_names), client-
// side, before any request reaches the model. No token spent, no ambiguity
// risk — an unclear or multi-match phrase is left to the ordinary Janet
// reply, never guessed at.
//
// What this deliberately does NOT do (documented, not silently skipped):
// arbitrary lateral moves from free text ("show me the Disney deal" typed
// cold, with no Property room open) are not routed here — those need the
// real tool-call contract in janet-chat.js/janet-ask.js, which is the larger
// capability this bridge stands in front of. Lateral moves FROM an open
// Property room are click-through doors (PropertyRoom.jsx) that push Janet's
// object-path context directly, no NL parsing required.

const LOOKUP_PATTERNS = [
  /^(?:let'?s\s+)?(?:look at|check|check in on)\s+where we are (?:with|on)\s+(.+?)\s*[.?!]*$/i,
  /^where are we with\s+(.+?)\s*[?.!]*$/i,
  /^(?:pull up|open|go to|show me)\s+(?:the\s+)?property\s+(.+?)\s*[.?!]*$/i,
]

/** Returns the extracted property-name phrase, or null if the message isn't
 * a property-lookup request. Deterministic — never a guess at intent. */
export function matchPropertyLookupIntent(text) {
  const trimmed = String(text || '').trim()
  if (!trimmed) return null
  for (const re of LOOKUP_PATTERNS) {
    const m = trimmed.match(re)
    if (m && m[1] && m[1].trim().length >= 2) return m[1].trim()
  }
  return null
}

/**
 * Escapes the SQL LIKE/ILIKE metacharacters (`%`, `_`) — and the backslash
 * that escapes them — in a value that must be matched LITERALLY inside an
 * ilike pattern. Postgres's default LIKE escape character is `\`, so a
 * literal backslash must be doubled FIRST or a user-supplied backslash would
 * itself start escaping the characters that follow it.
 *
 * Without this, a property name containing `%` or `_` is not just harmless
 * unmatched punctuation — those characters are themselves wildcards inside
 * the pattern this function builds (`%<name>%`), so an unescaped `%` inside
 * the name silently matches ANY substring at that position (e.g. a lookup
 * for "100% Wolf" would also match "100X Wolf", "100 anything Wolf" — any
 * text at all) and an unescaped `_` matches any single character (2026-09-29
 * repair, issue #170 finding 7). Escaping keeps the OUTER `%...%` wildcards —
 * the intended substring search — while making every metacharacter INSIDE
 * the extracted name literal data.
 */
function escapeLikeLiteral(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_')
}

/**
 * Runs the property-lookup search against the record. The extracted phrase
 * is a valid property name that may contain ANY punctuation — including
 * comma, parentheses, quotes/apostrophes — because that is what real titles
 * look like ("Around Disney's Worlds (International Cut), Vol. 2" is a
 * legitimate name). It must never be spliced into a raw PostgREST `.or()`
 * filter string: `.or()` parses its whole argument as filter grammar, where
 * comma separates conditions and parentheses nest logical groups, so a name
 * containing either would corrupt the query (2026-09-29 repair, issue #170
 * finding 6). `.ilike(column, pattern)` takes the pattern as a single
 * column's operator VALUE — never parsed as filter grammar, URL-encoded like
 * any other query-string value — so the name is always treated strictly as
 * search data. The name itself is further escaped (`escapeLikeLiteral`) so
 * any `%`/`_`/`\` it contains is literal text rather than a LIKE wildcard —
 * only the two wildcards THIS function adds, wrapping the whole name, drive
 * the substring search.
 *
 * One column suffices: `search_names` already carries the title plus every
 * alias for a property (`set_entity_search_names`,
 * `20260722_entity_rename_aliases.sql`, trigger arg `'title'`), which is the
 * same "title + alias" coverage the old two-column `.or()` provided — see
 * `usePropertyRights.js`'s own `search_names`-only property search and
 * `dealPickerSearch.js` for the identical established primitive on other
 * entities. The candidate/ambiguity doctrine is unchanged downstream in
 * `classifyPropertyMatches`.
 */
export function searchPropertiesForLookup(supabase, query) {
  return supabase
    .from('properties')
    .select('id, title, status, work_form, logline')
    .ilike('search_names', `%${escapeLikeLiteral(query)}%`)
    .limit(8)
}

/**
 * Classifies a property search's results per the standing name-resolution
 * doctrine (nameMatch.js/CLAUDE.md 2026-08-06): a search produces
 * CANDIDATES, never the answer. Exactly one usable hit resolves; anything
 * else is left to a human choice, never a guess.
 */
export function classifyPropertyMatches(rows) {
  const usable = (rows || []).filter((r) => r && r.id && r.title)
  if (usable.length === 0) return { status: 'none' }
  if (usable.length === 1) return { status: 'match', property: usable[0] }
  return { status: 'ambiguous', candidates: usable.slice(0, 6) }
}
